import {BusinessContactFieldChange, BusinessContactHistoryEntry} from './domain/types';

const CONTACT_CREATED = 'Contact created.';
/**
 * The opening of the status sentence. Exported so the panel can set the value that follows it in
 * bold, the way the mockup does, without matching on the whole sentence.
 */
export const STATUS_SENTENCE_LEAD = 'Changed status to ';
const STATUS_INACTIVE = `${STATUS_SENTENCE_LEAD}Inactive.`;
const REACTIVATED = 'Reactivated contact.';

/**
 * One sentence per group of fields, in the order the panel reads them. A group with several fields
 * gives one sentence however many of them changed: a person's name is one thing to the reader.
 */
const SENTENCE_BY_FIELDS: {fields: string[]; sentence: string}[] = [
    {fields: ['inactiveReason'], sentence: 'Updated inactive reason.'},
    {fields: ['businessFunction'], sentence: 'Updated business function(s).'},
    {fields: ['notesForCcs'], sentence: 'Updated notes for CCS.'},
    {fields: ['firstName', 'lastName', 'suffix'], sentence: 'Updated name.'},
    {fields: ['email'], sentence: 'Updated email.'},
    {fields: ['title'], sentence: 'Updated title.'},
    {fields: ['contactPhone'], sentence: 'Updated contact phone.'},
];

const NAMED_FIELDS = new Set([
    'inactive',
    ...SENTENCE_BY_FIELDS.reduce<string[]>((all, rule) => all.concat(rule.fields), []),
]);

/** mailingPostalCode reads as "mailing postal code". */
const humanise = (field: string): string => field.replace(/([A-Z])/g, ' $1').toLowerCase();

const isReactivation = (inactive: BusinessContactFieldChange | undefined): boolean =>
    inactive !== undefined && inactive.after !== 'true';

/**
 * The lines the panel shows for one history entry (BHPNPP-3248). The status change comes first, then
 * the fields the form edits in a fixed order, then any field the backend tracks that the form does
 * not, spelt out from its property name.
 *
 * <p>A blanked inactive reason is folded into a reactivation: the form clears the reason when a
 * contact comes back, so the clear is part of that one event and not a second change.
 */
export const historySentences = (entry: Pick<BusinessContactHistoryEntry, 'operation' | 'changes'>): string[] => {
    if (entry.operation === 'CREATED') {
        return [CONTACT_CREATED];
    }

    const changed = new Set(entry.changes.map((change) => change.field));
    const inactive = entry.changes.find((change) => change.field === 'inactive');
    const reactivated = isReactivation(inactive);

    const sentences: string[] = [];
    if (inactive) {
        sentences.push(reactivated ? REACTIVATED : STATUS_INACTIVE);
    }
    SENTENCE_BY_FIELDS.forEach(({fields, sentence}) => {
        if (fields.includes('inactiveReason') && reactivated) {
            return;
        }
        if (fields.some((field) => changed.has(field))) {
            sentences.push(sentence);
        }
    });
    entry.changes
        .filter((change) => !NAMED_FIELDS.has(change.field))
        .forEach((change) => sentences.push(`Updated ${humanise(change.field)}.`));

    return sentences;
};
